import axios, { isAxiosError, type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { useUser } from '@/hooks/queries/auth';
import { authHelper } from '@/lib/localStore';
import { APP_CONFIG } from '@/config/app';
import { ENV } from '@/config/env';
import {
  AUTH_SESSION_REDIRECT_PENDING_FLAG,
  DEFAULT_TENANT_ID,
  LOGOUT_IN_PROGRESS_FLAG,
} from '@/config/constants';
import { getSafePostLoginRedirectFromWindow } from '@/lib/authRedirect';
import {
  ensureSessionFresh,
  isAccessTokenStale,
  refreshSessionToken,
  waitForSessionRefreshInFlight,
} from '@/lib/sessionRefresh';
import { toast } from 'sonner';

/**
 * Constants for token refresh handling
 */
const MAX_REFRESH_TOKEN_RETRIES = 2;

/**
 * Tracks in-flight requests by "METHOD:url" key so duplicate requests to the
 * same endpoint can be cancelled before the new one is sent.
 */
const pendingRequests = new Map<string, AbortController>();

function getRequestKey(config: AxiosRequestConfig): string {
  return `${(config.method ?? 'get').toUpperCase()}:${config.url}`;
}

function removePendingRequest(key: string): void {
  pendingRequests.delete(key);
}
let refreshTokenRequestCount = 0;

const isLogoutInProgress = (): boolean => {
  return window.sessionStorage.getItem(LOGOUT_IN_PROGRESS_FLAG) === 'true';
};

/**
 * Reset refresh token request count
 */
function resetRefreshTokenCount(): void {
  refreshTokenRequestCount = 0;
}

/**
 * Increment refresh token request count
 */
function incrementRefreshTokenCount(): void {
  refreshTokenRequestCount++;
}

/**
 * Base URL for the API
 * @constant
 */
const BASE_URL = ENV.API_URL;

/**
 * Custom Axios instance configured with base URL and default headers
 * @constant
 */
export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: APP_CONFIG.API.TIMEOUT,
});

/**
 * Request interceptor
 * Add any auth tokens or additional headers here
 */
api.interceptors.request.use(
  async (config) => {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    if (config.url?.includes('login')) {
      return config;
    }
    config.headers['X-Tenant-Id'] = DEFAULT_TENANT_ID;

    // Cancel any in-flight request to the same endpoint and replace it.
    // GET-only: several endpoints (e.g. update-parent-child-item) share one URL across
    // different resources, distinguished only by request body. Deduping those by URL would
    // abort a concurrent, unrelated mutation — and a cancelled request's promise never
    // resolves (see the response interceptor below), so any caller awaiting it (e.g.
    // Promise.all over several task assignments) would hang forever with no error surfaced.
    if ((config.method ?? 'get').toUpperCase() === 'GET') {
      const key = getRequestKey(config);
      const existing = pendingRequests.get(key);
      if (existing) {
        existing.abort();
        pendingRequests.delete(key);
      }
      const controller = new AbortController();
      // Combine our dedup signal with any caller signal (e.g. TanStack Query's signal)
      const signals: AbortSignal[] = [controller.signal];
      if (config.signal instanceof AbortSignal) signals.push(config.signal);
      config.signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];
      pendingRequests.set(key, controller);
    }

    if (!isLogoutInProgress() && authHelper.refreshToken) {
      await waitForSessionRefreshInFlight();
      if (isAccessTokenStale()) {
        await ensureSessionFresh({ reason: 'request' });
      }
    }

    const token = authHelper.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Handle refresh token after server returns 4002 or 4001 or request code is 401 or 402
 * @param error - The error object from axios response.
 * @returns The response or error
 * @description If we get invalid token on refresh token api 2 times in a row, redirect to login
 * else retry the request with updated token
 */
const handleRefreshToken = async (error: AxiosError): Promise<AxiosResponse | Promise<AxiosError>> => {
  if (isLogoutInProgress()) {
    return Promise.reject(error);
  }

  // if we get invalid token on refresh token api 2 times in a row, redirect to login
  if (refreshTokenRequestCount >= MAX_REFRESH_TOKEN_RETRIES) {
    return handleInvalidToken(error);
  }
  
  incrementRefreshTokenCount();
  const refreshToken = authHelper.refreshToken;
  
  if (!refreshToken) {
    return handleInvalidToken(error);
  }
  
  try {
    const refreshed = await refreshSessionToken();
    if (refreshed) {
      resetRefreshTokenCount();
      // Retry the original request with new token
      if (error.config) {
        return api.request(error.config as AxiosRequestConfig);
      }
    }
    return handleInvalidToken(error);
  } catch {
    return handleInvalidToken(error);
  }
};

/**
 * Handle invalid token after server returns 4001
 * @param error - The error object from axios response.
 * @returns The response or error
 * @description Redirect to login and clear local storage
 */
const handleInvalidToken = (error: AxiosError) => {
  if (isLogoutInProgress()) {
    return Promise.reject(error);
  }

  if (window.sessionStorage.getItem(AUTH_SESSION_REDIRECT_PENDING_FLAG) === 'true') {
    return Promise.reject(error);
  }

  // Avoid infinite loops: if we're already forcing a login, don't redirect again
  if (
    window.location.pathname === '/authentikLogin' &&
    new URLSearchParams(window.location.search).get('forceLogin') === 'true'
  ) {
    return Promise.reject(error);
  }

  window.sessionStorage.setItem(AUTH_SESSION_REDIRECT_PENDING_FLAG, 'true');
  useUser.getState().logoutUser();
  toast.info('Session expired, please login again');
  const redirectTo = getSafePostLoginRedirectFromWindow();
  // Force a fresh login challenge on the next authorize redirect
  const search = new URLSearchParams({
    forceLogin: 'true',
    redirectTo: redirectTo,
  });
  window.location.replace(`/authentikLogin?${search.toString()}`);
  return Promise.reject(error);
};

/**
 * Response interceptor
 * Handle common response scenarios and errors
 */
api.interceptors.response.use(
  (response) => {
    removePendingRequest(getRequestKey(response.config));
    return response;
  },
  (error: AxiosError) => {
    if (error.config) {
      removePendingRequest(getRequestKey(error.config));
    }

    // Request was intentionally cancelled by our deduplication logic — swallow silently
    if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return new Promise(() => {}); // never resolves; caller is superseded
    }

    // Handle specific error cases
    if (isAxiosError(error)) {
      const response = error.response as AxiosResponse<{ code: number; message: string }>;
      // Handle bad request
      if (response?.data?.code) {
        if (response?.data.code === 4002) {
          // Handle refresh token expired
          return handleRefreshToken(error);
        } else if (response?.data.code === 4001) {
          return handleInvalidToken(error);
        }
      }
      switch (response?.status) {
        case 401:
        case 402:
          // Handle unauthorized, if refresh token is not valid, redirect to login
          return handleRefreshToken(error);
        case 403:
          // Handle forbidden
          break;
        case 404:
          // Handle not found
          break;
        case 500:
          // Handle server error
          break;
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/**
 * Custom error type for API errors
 */
interface ApiError extends Error {
  code?: string | number;
  status?: number;
  response?: AxiosResponse;
}

/**
 * Handle error
 * @param error - The error object from axios response or any error
 * @throws {ApiError} - Custom error object with code, message, and status
 * @description Handle error and throw it with proper typing
 */
export const handleError = (error: unknown): never => {
  // If the error is an AxiosError, extract relevant information
  if (isAxiosError(error)) {
    const apiError: ApiError = new Error(
      error.response?.data?.message || error.message || 'An error occurred'
    );
    apiError.name = 'AxiosError';
    apiError.code = error.response?.data?.code || error.code;
    apiError.status = error.response?.status;
    apiError.response = error.response;
    apiError.stack = error.stack;
    throw apiError;
  }
  
  // For non-Axios errors, create a generic error
  if (error instanceof Error) {
    const apiError: ApiError = new Error(error.message);
    apiError.name = error.name;
    apiError.stack = error.stack;
    throw apiError;
  }
  
  // For unknown error types
  const apiError: ApiError = new Error('An unknown error occurred');
  apiError.name = 'UnknownError';
  throw apiError;
};
